import { zodResolver } from '@hookform/resolvers/zod';
import { passwordSchema } from '@familywallet/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { FormError } from '../components/FormError';
import { PublicLayout } from '../components/PublicLayout';
import { Button, buttonVariants } from '../components/ui/button';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';

const newAccountSchema = z.object({ password: passwordSchema });
const existingAccountSchema = z.object({ password: z.string().min(1).max(128) });
type PasswordForm = { password: string };

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const preview = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => api.invitations.preview(token),
    retry: false,
  });
  const existing = preview.data?.existingAccount ?? false;
  const form = useForm<PasswordForm>({
    resolver: zodResolver(existing ? existingAccountSchema : newAccountSchema),
  });
  const accept = useMutation({
    mutationFn: (values: PasswordForm) =>
      api.invitations.accept({ token, password: values.password }),
    onSuccess: () => navigate('/', { replace: true }),
  });

  if (preview.isPending) {
    return <PublicLayout title={t('app.loading')}>{null}</PublicLayout>;
  }
  if (preview.isError) {
    return (
      <PublicLayout title={t('app.name')}>
        <FormError error={preview.error} />
        <Link to="/login" className={buttonVariants({ variant: 'secondary', block: true })}>
          {t('acceptInvite.goToLogin')}
        </Link>
      </PublicLayout>
    );
  }

  const invitation = preview.data;
  const intro = invitation.inviterDisplayName
    ? t('acceptInvite.intro', {
        inviter: invitation.inviterDisplayName,
        name: invitation.memberDisplayName,
      })
    : t('acceptInvite.introNoInviter', { name: invitation.memberDisplayName });

  return (
    <PublicLayout title={t('acceptInvite.title', { family: invitation.familyName })} intro={intro}>
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((v) => accept.mutate(v))}
      >
        <FormError error={accept.error} />
        <p className="text-sm text-muted">
          {existing ? t('acceptInvite.existingAccount') : t('acceptInvite.newAccount')}
        </p>
        <Field label={t('fields.email')}>
          <Input type="email" value={invitation.email} readOnly autoComplete="username" />
        </Field>
        <Field
          label={existing ? t('fields.password') : t('fields.newPassword')}
          hint={existing ? undefined : t('fields.passwordHint')}
          error={form.formState.errors.password}
          kind="password"
        >
          <Input
            type="password"
            autoComplete={existing ? 'current-password' : 'new-password'}
            {...form.register('password')}
          />
        </Field>
        <Button type="submit" block disabled={accept.isPending}>
          {accept.isPending ? t('acceptInvite.submitting') : t('acceptInvite.submit')}
        </Button>
      </form>
    </PublicLayout>
  );
}
